/**
 * @class BreadcrumbsSnippet
 * @extends HTMLElement
 *
 * @description
 * A customized built-in element extending `<nav>` to manage and display
 * breadcrumbs on a product detail page. This class checks the user's referrer
 * to see if they arrived from a `/collections/...` page. If so, it fetches
 * that collection page to retrieve an accurate title for the breadcrumb
 * link. Otherwise, it simply un-hides the breadcrumbs.
 */
class BreadcrumbsSnippet extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initBreadcrumbs();
  }

  /**
   * Checks the referrer for a `/collections/...` pattern. If found,
   * fetches that page to extract the collection title and updates
   * the breadcrumb link accordingly. Always ensures breadcrumbs are
   * un-hidden if referrer conditions are not met or the request fails.
   */
  async initBreadcrumbs() {
    const referrer = document.referrer;
    const $breadcrumbs = this;

    if (referrer.includes('/collections/')) {
      const collectionHandle = referrer.split('/collections/')[1].split('/')[0].split('?')[0];
      const $breadcrumbLink = $breadcrumbs.querySelector('#collection-link');

      try {
        const response = await fetch(referrer);
        if (!response.ok) throw new Error('Network response was not ok');

        const data = await response.text();
        const $markup = new DOMParser().parseFromString(data, 'text/html');
        const collectionTitle =
          $markup.querySelector('#MainContent .page-header .header-content h1')?.innerText;

        if (collectionTitle && $breadcrumbLink) {
          $breadcrumbLink.href = '/collections/' + collectionHandle;
          $breadcrumbLink.textContent = collectionTitle;
        }
        $breadcrumbs.classList.remove('is-hidden');
      } catch (error) {
        console.error('Error fetching JSON:', error);
        $breadcrumbs.classList.remove('is-hidden');
      }
    } else {
      $breadcrumbs.classList.remove('is-hidden');
    }
  }
}

customElements.define('breadcrumbs-snippet', BreadcrumbsSnippet, {
  extends: 'nav'
});

/**
 * @class ProductTabs
 * @extends HTMLElement
 *
 * @description
 * A customized built-in element extending `<div>` that handles the
 * tabbing logic on a PDP: switching the visible tab content when
 * the user clicks on a corresponding tab button.
 */
class ProductTabs extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initTabs();
  }

  /**
   * Initializes the tab logic:
   * - Finds all .tabs__nav button elements
   * - Stores the currently active tab and tab content
   * - On click, deactivates the old tab, activates the new one
   */
  initTabs() {
    const tabsButtons = this.querySelectorAll('.tabs__nav button');
    let activeTab = this.querySelector('.tabs__nav .is-active');
    let activeTabContents = this.querySelector('.tab.is-active');

    tabsButtons.forEach((tabLink) => {
      tabLink.addEventListener('click', (event) => {
        const { target } = event;
        if (target.classList.contains('is-active')) return;
        const targetTabContent = this.querySelector(target.dataset.target);

        // Deactivate previous tab
        if (activeTab) activeTab.classList.remove('is-active');
        if (activeTabContents) activeTabContents.classList.remove('is-active');
        // Activate the newly clicked tab
        target.parentElement.classList.add('is-active');
        targetTabContent.classList.add('is-active');
        // Update references
        activeTab = target.parentElement;
        activeTabContents = targetTabContent;
      });
    });
  }
}

customElements.define('product-tabs', ProductTabs);

/**
 * @class ProductGallery
 * @extends HTMLElement
 *
 * @description
 * A customized built-in element that manages the main product gallery
 * (Slick carousel) and its thumbnails, plus the click events for zoom and modal.
 */
class ProductGallery extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initGallery();
  }

  initGallery() {
    this.timeouts = [];
    this.mainSlickSlider = $(this); // jQuery object for Slick
    this.mainSlider = this;        // plain DOM reference
    this.thumbnailsSlider = document.querySelector('#product-gallery-thumbs');

    this.initMainSlickSlider();
    this.bindImageClickEvent();
    this.bindModalCloseEvent();
    this.bindThumbsClickEvent();
    this.bindThumbsMoveEvent();
  }

  initMainSlickSlider() {
    // Listen for Slick’s init event
    this.mainSlickSlider.on('init', (event, slick) => {
      this.timeouts.forEach(clearTimeout);
      this.timeouts.length = 0;
      this.timeouts.push(
        setTimeout(() => {
          const zoomHolders = document.querySelectorAll('zoom-holder');
          if (zoomHolders.length && this.mainSlickSlider !== undefined) {
            zoomHolders.forEach((zoomHolder) => {
              zoomHolder.init(this.mainSlickSlider, slick);
            });
          }
        }, 0)
      );
    });

    this.mainSlickSlider.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      dots: false,
      mobileFirst: true,
    });

    this.setContentPadding();
  }

  setContentPadding() {
    const modalContent = this.closest('.product-modal__content');
    const modalAside = this.closest('.product-modal').querySelector('.product-modal__aside');
    const modalAsideWidth = modalAside.getBoundingClientRect().width;
    const contentPadding = modalAsideWidth + 50;
    modalContent.style.setProperty('--content-padding', `${contentPadding}px`);
  }

  bindImageClickEvent() {
    document.querySelectorAll('#product-large-images .product-media').forEach((image) => {
      image.addEventListener('click', () => {
        document.querySelector('body').classList.add('no-scroll');

        let slideIndex = image.dataset.mediaPosition;
        const modal = document.querySelector('.product-modal');
        slideIndex--;

        const thumbElement = this.thumbnailsSlider.querySelectorAll('.product-media')[slideIndex];
        this.thumbSetToActive(thumbElement);

        this.mainSlickSlider.slick('slickGoTo', slideIndex, true);

        modal.classList.add('is-active');
        this.recalculateContainerWidth();
      });
    });
  }

  bindModalCloseEvent() {
    document.querySelector('.js-modal-close').addEventListener('click', (event) => {
      const modal = event.target.closest('.product-modal');
      if (modal) {
        modal.classList.remove('is-active');
        document.querySelector('body').classList.remove('no-scroll');
      }
    });
  }

  recalculateContainerWidth() {
    const zoomHolders = document.querySelectorAll('zoom-holder');
    zoomHolders.forEach((zoomHolder) => {
      zoomHolder.recalculateContainerWidth();
    });
  }

  bindThumbsClickEvent() {
    this.thumbnailsSlider.addEventListener('click', (event) => {
      const currentActive = this.thumbnailsSlider.querySelector('.product-media.is-active');
      const target = event.target.closest('.product-media');
      if (target) {
        this.thumbRemoveActive(currentActive);
        this.thumbSetToActive(target);
        this.mainSlickSlider.slick('slickGoTo', target.dataset.mediaPosition - 1, true);
      }
    });
  }

  bindThumbsMoveEvent() {
    this.mainSlickSlider.on('beforeChange', (event, slick, currentSlide, nextSlide) => {
      const currentActive = this.thumbnailsSlider.querySelector('.product-media.is-active');
      const nextSlideElement = this.thumbnailsSlider.querySelectorAll('.product-media')[nextSlide];

      this.thumbRemoveActive(currentActive);
      this.thumbSetToActive(nextSlideElement);
    });
  }

  thumbRemoveActive(element) {
    const zoomInHolder = this.mainSlider.querySelector('.zoom-in');
    if (zoomInHolder) {
      zoomInHolder.classList.remove('zoom-in');
      zoomInHolder.querySelector('zoom-holder').zoom = 1.5;
      zoomInHolder.querySelector('zoom-holder').tapCount = 0;
      zoomInHolder.querySelector('zoom-holder').zoomActive = false;
      zoomInHolder.querySelector('zoom-holder').toggleCarouselDrag(true);
    }
    element.classList.remove('is-active');
  }

  thumbSetToActive(element) {
    element.classList.add('is-active');
  }
}

customElements.define('product-gallery', ProductGallery);

/**
 * @class ZoomHolder
 * @extends HTMLElement
 *
 * @description
 * Handles pinch/zoom, drag, and now hover-based movement inside the product media slides.
 */
class ZoomHolder extends HTMLElement {
  constructor() {
    super();

    this.zoom = 2.5;
    this.zoomActive = false;
    this.xImagePosition = 0;
    this.yImagePosition = 0;
    this.container = this.closest('.product-media');
    this.containerWidth = this.container.getBoundingClientRect().width;
    this.image = this.querySelector('.product__img');
    this.imageWidth = this.image.getBoundingClientRect().width;
    this.imageHeight = this.image.getBoundingClientRect().height;

    // double-tap detection
    this.doubleTapDelay = 300;
    this.tapCount = 0;
    this.lastTapTime = 0;
    this.lastTouchEnd = 0;
    this.touchStartDistance = 0;

    // offset-based dragging
    this.offsetX = 0;
    this.offsetY = 0;
    this.lastPointerX = 0;
    this.lastPointerY = 0;
    this.isDragging = false; // When true, user is actively dragging (desktop)

    // Ensure the image is loaded before calculating dimensions
    this.image.addEventListener('load', () => {
      this.imageWidth = this.image.getBoundingClientRect().width;
      this.imageHeight = this.image.getBoundingClientRect().height;
      this.zoom = Math.max(this.containerWidth / this.imageWidth, 2);
      this.setImageSize();
    });

    if (this.image.complete) {
      this.image.dispatchEvent(new Event('load'));
    }

    // Change zoom for mobile
    if (this.isMobile()) {
      this.zoom = 1.1;
    }
  }

  init(carouselHolder, instanceCarousel) {
    this.instanceCarousel = instanceCarousel;
    this.slider = carouselHolder;

    if (this.instanceCarousel) {
      this.dragEvent();
    }
    this.events();
  }

  recalculateContainerWidth() {
    this.containerWidth = this.container.getBoundingClientRect().width;
    this.zoom = Math.max(this.containerWidth / this.imageWidth, 2);
    this.setImageSize();
  }

  isMobile() {
    const mobileQuery = `(max-width: 991px)`;
    return window.matchMedia(mobileQuery).matches;
  }

  events() {
    this.setImageSize();
    window.addEventListener('resize', this.onResizeEvent.bind(this));

    // MOUSE / CLICK EVENTS (desktop)
    this.addEventListener('click', this.onClickEvent.bind(this));
    this.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.addEventListener('mouseup', this.onMouseUp.bind(this));
    this.addEventListener('mouseleave', this.onMouseUp.bind(this));

    // TOUCH EVENTS (mobile)
    this.addEventListener('touchstart', this.onTouchStart.bind(this));
    this.addEventListener('touchmove', this.onTouchMove.bind(this));
    this.addEventListener('touchend', this.onTouchEnd.bind(this));
  }

  onResizeEvent() {
    // Reset zoom if needed
    if (Object.keys(this.instanceCarousel).length === 0 && this.instanceCarousel.constructor === Object) {
      this.classList.remove('zoom-in');
    } else {
      if (this.isDragging) return;
      this.parentElement.classList.remove('zoom-in');
      this.toggleCarouselDrag(true);
    }

    this.zoomActive = false;
    this.offsetX = 0;
    this.offsetY = 0;
    this.setImageSize();
  }

  onClickEvent() {
    if (this.isMobile()) return; // On mobile, we use double-tap or pinch

    // Add a small threshold check to differentiate a click vs. a drag
    const moveThreshold = 5; // px allowance for small pointer movements
    const movedX = Math.abs(this.coords.xDown - this.coords.xUp);
    const movedY = Math.abs(this.coords.yDown - this.coords.yUp);

    if (movedX > moveThreshold || movedY > moveThreshold) {
      // The user actually dragged; do NOT toggle zoom
      return;
    }

    // If we are still dragging by the time click fires, skip
    if (this.isDragging) return;

    // Toggle zoom
    if (Object.keys(this.instanceCarousel).length === 0 && this.instanceCarousel.constructor === Object) {
      // standalone usage
      this.classList.toggle('zoom-in');
    } else {
      // inside Slick
      this.parentElement.classList.toggle('zoom-in');
      this.toggleCarouselDrag(this.parentElement.classList.contains('zoom-in') ? false : true);
    }
    this.zoomActive = !this.zoomActive;
  }

  // ====== DESKTOP MOUSE EVENTS =======
  onMouseDown(event) {
    if (!this.zoomActive) return;
    // If zoom is active, user can drag
    this.isDragging = true;
    this.lastPointerX = event.clientX;
    this.lastPointerY = event.clientY;
  }

  onMouseMove(event) {
    if (this.isMobile()) return;
    if (!this.zoomActive) return;

    if (this.isDragging) {
      // DRAG MODE
      const dx = event.clientX - this.lastPointerX;
      const dy = event.clientY - this.lastPointerY;

      this.offsetX += dx;
      this.offsetY += dy;
      this.clampOffset();

      this.zoomedImage.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px) translateZ(0)`;

      this.lastPointerX = event.clientX;
      this.lastPointerY = event.clientY;
    } else {
      // HOVER MODE
      this.hoverPan(event);
    }
  }

  onMouseUp() {
    this.isDragging = false;
  }

  /**
   * Allows the user to move the zoomed image by simply hovering
   * (when zoomActive is true but not dragging).
   */
  hoverPan(event) {
    const containerRect = this.container.getBoundingClientRect();
    // The fully zoomed image size
    const imageWidth = this.imageWidth * this.zoom;
    const imageHeight = this.imageHeight * this.zoom;

    // Mouse coordinates relative to container
    const mouseX = event.clientX - containerRect.left;
    const mouseY = event.clientY - containerRect.top;

    // Convert to ratio [0..1]
    const ratioX = mouseX / containerRect.width;
    const ratioY = mouseY / containerRect.height;

    // The maximum offset from 0 to -maxOffset
    const maxOffsetX = imageWidth - containerRect.width;
    const maxOffsetY = imageHeight - containerRect.height;

    // The image offset so that ratioX, ratioY maps to the correct portion
    let offsetX = -maxOffsetX * ratioX;
    let offsetY = -maxOffsetY * ratioY;

    // clamp so we don’t exceed container bounds
    offsetX = Math.max(Math.min(offsetX, 0), -maxOffsetX);
    offsetY = Math.max(Math.min(offsetY, 0), -maxOffsetY);

    // apply transform
    this.zoomedImage.style.transform = `translate(${offsetX}px, ${offsetY}px) translateZ(0)`;

    // optional: keep offsetX/offsetY in sync with drag
    // so there's no jump if user presses mouse down
    this.offsetX = offsetX;
    this.offsetY = offsetY;
  }

  // ====== MOBILE TOUCH EVENTS =======
  onTouchStart(event) {
    if (event.touches.length === 2) {
      // pinch start
      this.baseZoom = this.zoom;
      this.parentElement.classList.add('zoom-in');
      this.toggleCarouselDrag(false);
      this.zoomActive = true;
      this.touchStartDistance = this.getDistance(event.touches);

      // store the offsetX/offsetY as "base" in case we already scrolled
      this.baseOffsetX = this.offsetX;
      this.baseOffsetY = this.offsetY;
      // find midpoint in container coordinates
      const containerRect = this.container.getBoundingClientRect();
      const [t1, t2] = event.touches;
      const midX = ((t1.clientX + t2.clientX) / 2) - containerRect.left;
      const midY = ((t1.clientY + t2.clientY) / 2) - containerRect.top;
      this.pinchMidX = midX;
      this.pinchMidY = midY;
    } else if (event.touches.length === 1) {
      // normal single-finger => check double-tap
      this.doubleTap();
    }


    // We handle zoom toggle after doubleTap logic:
    this.touchStart().then(() => {
      // If we are in zoom mode & single finger, we can drag
      if (this.zoomActive && event.touches.length === 1) {
        this.isDragging = true;
        this.lastPointerX = event.touches[0].clientX;
        this.lastPointerY = event.touches[0].clientY;
      }
    });
  }

  onTouchMove(event) {
    if (event.touches.length === 2) {
      // pinch logic
      const currentDistance = this.getDistance(event.touches);
      const scale = currentDistance / this.touchStartDistance;
      // keep zoom within [1, 5]
      const newZoom = Math.max(Math.min((this.baseZoom * scale), 5), 1);
      const zoomRatio = newZoom / this.zoom;

      if (newZoom <= this.zoom && newZoom < 1.1) {
        // Center the image in the container
        const containerRect = this.container.getBoundingClientRect();
        const realWidth = this.imageWidth * newZoom;
        const realHeight = this.imageHeight * newZoom;

        this.offsetX = -((realWidth - containerRect.width) / 2);
        this.offsetY = -((realHeight - containerRect.height) / 2);

        this.zoomActive = false;
        this.toggleCarouselDrag(true);
        this.parentElement.classList.remove('zoom-in');
      } else {
        this.offsetX -= (this.pinchMidX * (zoomRatio - 1));
        this.offsetY -= (this.pinchMidY * (zoomRatio - 1));
      }

      this.zoom = newZoom;
      this.clampOffset();
      this.setImageSize();

      if (this.isDragging) {
        this.handleTouchDrag(event);
      }
    } else if (this.isDragging && this.zoomActive) {
      // single-finger pan
      this.handleTouchDrag(event);
    }
  }

  onTouchEnd() {
    if (this.isDragging) {
      this.isDragging = false;
    }
    this.lastTouchEnd = new Date().getTime();
  }

  handleTouchDrag(event) {
    const currentPointerX = event.touches[0].clientX;
    const currentPointerY = event.touches[0].clientY;

    const dx = currentPointerX - this.lastPointerX;
    const dy = currentPointerY - this.lastPointerY;

    this.offsetX += dx;
    this.offsetY += dy;
    this.clampOffset();

    this.zoomedImage.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px) translateZ(0)`;

    this.lastPointerX = currentPointerX;
    this.lastPointerY = currentPointerY;
  }

  // Dist between two touches
  getDistance(touches) {
    const [touch1, touch2] = touches;
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  touchStart() {
    return new Promise((resolve) => {
      setTimeout(() => {
        // If user is pinching (touchStartDistance > 0), skip the double-tap logic
        if (this.touchStartDistance > 0) {
          resolve('done');
          return;
        }

        // Double-tap toggles zoom
        if (this.tapCount === 2) {
          this.mobileZoomIn(2.5);
        } else if (this.tapCount === 1) {
          this.mobileZoomIn(1.5);
        }

        // If single tap ended up not toggling => zoom out
        if (this.tapCount === 0) {
          this.zoom = 1.1;
          this.parentElement.classList.remove('zoom-in');
          this.toggleCarouselDrag(true);
          this.zoomActive = false;
          this.offsetX = 0;
          this.offsetY = 0;
          this.setImageSize();
          if (this.zoomedImage) {
            this.zoomedImage.style.left = '0px';
            this.zoomedImage.style.top = '0px';
            this.zoomedImage.style.transform = 'translate(0px, 0px) translateZ(0)';
          }
        }

        resolve('done');
      }, 0);
    });
  }

  mobileZoomIn(zoom) {
     // Zoom in
     this.zoom = zoom;
     this.parentElement.classList.add('zoom-in');
     this.toggleCarouselDrag(false);
     this.zoomActive = true;
     this.setImageSize();

     // SHIFT TO TOP-CENTER
     const containerRect = this.container.getBoundingClientRect();
     const realWidth = this.imageWidth * this.zoom;
     this.offsetX = -((realWidth - containerRect.width) / 2);
     this.offsetY = 0;
     this.clampOffset();
     this.zoomedImage.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px) translateZ(0)`;
  }

  // Re-applies transform after resizing or pinch
  setImageSize() {
    const realWidth = this.imageWidth * this.zoom;
    const realHeight = this.imageHeight * this.zoom;

    this.zoomedImage = this.querySelector('.product__img-zoomed');
    if (!this.zoomedImage) return;

    this.zoomedImage.style.width = `${realWidth}px`;
    this.zoomedImage.style.height = `${realHeight}px`;

    this.clampOffset();
    this.zoomedImage.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px) translateZ(0)`;
  }

  clampOffset() {
    if (!this.zoomedImage) return;

    const containerRect = this.container.getBoundingClientRect();
    const realWidth = this.imageWidth * this.zoom;
    const realHeight = this.imageHeight * this.zoom;

    const maxOffsetX = realWidth - containerRect.width;
    const maxOffsetY = realHeight - containerRect.height;

    // offsetX < 0 => image is dragged left
    if (this.offsetX > 0) this.offsetX = 0;
    if (this.offsetX < -maxOffsetX) this.offsetX = -maxOffsetX;

    // offsetY < 0 => image is dragged up
    if (this.offsetY > 0) this.offsetY = 0;
    if (this.offsetY < -maxOffsetY) this.offsetY = -maxOffsetY;
  }

  toggleCarouselDrag(isEnabled) {
    // Stop slick from swiping or dragging behind the scenes
    this.slider.slick('slickSetOption', 'draggable', isEnabled);
    this.slider.slick('slickSetOption', 'swipe', isEnabled);
    this.slider.slick('slickSetOption', 'touchMove', isEnabled);
  }

  dragEvent() {
    // Track initial pointerdown coords
    this.addEventListener('pointerdown', (event) => {
      this.coords = this.coords || {};
      this.coords.xDown = event.clientX;
      this.coords.yDown = event.clientY;
    });

    // Track final pointerup coords
    this.addEventListener('pointerup', (event) => {
      this.coords.xUp = event.clientX;
      this.coords.yUp = event.clientY;
    });
  }

  doubleTap() {
    const currentTime = Date.now();
    const timeDifference = currentTime - this.lastTapTime;

    if (timeDifference < this.doubleTapDelay && timeDifference > 0) {
      if (this.tapCount === 2) {
        this.tapCount = 0;
      } else if (this.zoom >= 2.5 && this.tapCount > 1) {
        this.tapCount = 2;
      } else {
        this.tapCount++;
      }

      this.touchStartDistance = 0;
    }

    this.lastTapTime = currentTime;
  }
}

customElements.get('zoom-holder') ||
  customElements.define('zoom-holder', ZoomHolder);

/**
 * Sets up a MutationObserver on the .reviews-io-ugc-widget DOM node
 * to detect changes from a third-party vendor's widget (Reviews.io).
 * Once mutations are observed, we handle custom size-guide behavior
 * in response to the "How It Fits" slider in the Reviews widget.
 *
 * @listens DOMContentLoaded
 */
document.addEventListener('DOMContentLoaded', () => {
  const targetNode = document.querySelector('.reviews-io-ugc-widget');

  const handleMutation = () => {
    const reviewsWidget = document.querySelector('#ReviewsWidget');
    const sizeGuideMessage = document.querySelector('.js-size-guide-message');
    if (!reviewsWidget || !sizeGuideMessage) return;

    const sizeUp = sizeGuideMessage.querySelector('.js-product__size-up');
    const sizeDown = sizeGuideMessage.querySelector('.js-product__size-down');

    if (document.querySelector('.ruk-rating-snippet-count')) {
      const totalReviews = document.querySelector('.ruk-rating-snippet-count').innerText;
      const reviewsCount = parseInt(totalReviews.match(/\d+/)[0]);
      const minimumReviews = parseInt(sizeGuideMessage.dataset.minimumReviews);
      if (reviewsCount < minimumReviews) return;
    } else {
      return;
    }

    const reviewsWidgetHeading = reviewsWidget.querySelectorAll('.ElementsWidget__header .R-TextHeading');
    reviewsWidgetHeading.forEach((heading) => {
      if (heading.innerHTML.toLowerCase() === 'how it fits') {
        handleSizeGuidance(heading, sizeGuideMessage, sizeUp, sizeDown);
      }
    });
  };

  const handleSizeGuidance = (heading, sizeGuideMessage, sizeUp, sizeDown) => {
    const ifFitsPosition = heading.parentElement.querySelector('.R-SliderIndicator__button').style.left;
    const positionNumber = parseFloat(ifFitsPosition);
    const sizeGuidance = sizeGuideMessage.dataset.sizeGuidance;

    if (ifFitsPosition === '0%') {
      sizeDown.classList.remove('hidden');
    } else if (ifFitsPosition === '100%') {
      sizeUp.classList.remove('hidden');
    } else if (positionNumber > 45 && positionNumber < 55 && sizeGuidance) {
      if (sizeGuidance.includes('large_in_size')) {
        sizeDown.classList.remove('hidden');
      } else if (sizeGuidance.includes('small_in_size')) {
        sizeUp.classList.remove('hidden');
      }
    }
  };

  const observer = new MutationObserver(handleMutation);
  observer.observe(targetNode, { attributes: true, childList: true, subtree: true });
});
